import type { SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import type { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { set } from '@ember/object'
import type EmberArray from '@ember/array'
import type { Identifiable } from 'teacher-ui-client/components/data-operations/table/records-selector'

/**
 * Extracts keyof object that are strings.
 * Useful when we want to use the key as an object index (keyof returns a union
 * of string | number | symbol however symbol cannot index an object and will
 * cause a type error).
 */
type StringKeyOf<T extends object> = keyof T & string

export interface DataOperationsConfig<RecordType extends Identifiable> {
  data: RecordType[] | EmberArray<RecordType>
  sortCol?: string
  sortDir?: SortDirection
  sortCompare?: SortType
  currentPage?: number
  recordsPerPage?: number
  // the id of a given record might be a string or number but we maintain the
  // selectedIds as a string array
  selectedIds?: string[]
}

export interface DataOperationsController {
  sortCol?: string
  sortDir?: SortDirection
  sortCompare?: SortType
  currentPage?: number
  recordsPerPage?: number
  selectedIds?: string[] | number[]
}

/**
 * The purpose of the proxy is so that the class implementing
 * DataOperationsController/DataOperationsConfig can have it's table data on a
 * property with a custom key other than 'data' and the recordsPerPage property
 * can have a default value if it isn't defined on the class.
 **/
export function buildDataOperationsConfigProxy<C extends DataOperationsController>(controller: C) {
  const handler = new DataOperationsHandler<C>()
  return new Proxy<C>(controller, handler)
}

class DataOperationsHandler<C extends DataOperationsController> {
  get<Key extends StringKeyOf<C>>(target: C, property: Key) {
    if (property === 'recordsPerPage') {
      return target[property] || 10
    }
    return target[property]
  }

  set<Key extends StringKeyOf<C>>(target: C, property: Key, value: C[Key]) {
    set(target, property, value)
    return true
  }
}
